/* eslint-disable react/display-name */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import PropTypes from "prop-types";
import React, { useMemo, forwardRef } from "react";
import Image from "../Image";
import { ImageSourcesType } from "../Image/types";

const getDays = (sponsorExpDate) =>
  new Date(sponsorExpDate).getTime() / (1000 * 60 * 60 * 24) -
  new Date().getTime() / (1000 * 60 * 60 * 24);

const dateInPast = (firstDate, secondDate) => {
  if (firstDate.setHours(0, 0, 0, 0) <= secondDate.setHours(0, 0, 0, 0)) {
    return true;
  }
  return false;
};

function secondsToTime(dur) {
  let hrs = Math.floor(dur / 3600);
  let min = Math.floor((dur - hrs * 3600) / 60);
  let sec = dur - hrs * 3600 - min * 60;

  if (hrs > 0) {
    hrs = `${hrs}:`;

    if (min < 10) {
      min = `0${min}`;
    }
  } else {
    hrs = "";
  }

  if (sec < 10) {
    sec = `0${sec}`;
  }

  return `${hrs}${min}:${sec}`;
}

const tagClasses = "p-xs bg-black bg-opacity-60 text-white gn-text-xs rounded";

const VideoCard = forwardRef(
  (
    {
      mode,
      title,
      slug,
      subtitle,
      description,
      images,
      sponsorExpDate,
      duration,
      handleClick,
    },
    ref,
  ) => {
    const daysLeft = useMemo(() => getDays(sponsorExpDate), [sponsorExpDate]);
    const isPast = useMemo(
      () => dateInPast(new Date(), new Date(sponsorExpDate)),
      [sponsorExpDate],
    );
    const isDark = mode === "dark";

    return (
      <div
        data-testid="video-card"
        ref={ref}
        className="cursor-pointer"
        onClick={handleClick}
      >
        {/* Thumbnail Container */}
        <div
          className={`
          relative aspect-w-16 aspect-h-9 bg-gray-700 rounded group overflow-hidden transition-shadow
          ${
            isDark
              ? "shadow-darkSM hover:shadow-darkLG"
              : "shadow-lightSM hover:shadow-lightMD"
          }
        `}
        >
          {/* Thumbnail */}
          <Image
            className="absolute inset-0 object-contain"
            sources={images.tile}
            alt={slug}
          />

          {/* Hoverable content */}
          <div
            data-testid="video-card-info"
            className="opacity-0 absolute inset-0 p-sm bg-black bg-opacity-80 transition-opacity duration-500 lg:group-hover:opacity-100"
          >
            {description ? (
              <h3
                data-testid="video-card-description"
                className="gn-text-sm mb-sm text-white"
              >
                {description}
              </h3>
            ) : null}
            {subtitle ? (
              <p
                data-testid="video-card-subtitle"
                className="gn-text-xs text-white"
              >
                {subtitle}
              </p>
            ) : null}
          </div>

          {/* Sponsorship & Duration */}
          <div
            className={`absolute flex ${
              sponsorExpDate && isPast ? "justify-between" : "justify-end"
            }`}
          >
            {sponsorExpDate ? (
              isPast ? (
                <span
                  className={`${tagClasses} self-start mt-xs ml-xs truncate mr-sm transition-opacity lg:group-hover:opacity-0`}
                  data-testid="video-card-sponsorship"
                >
                  {daysLeft > 14
                    ? "Sponsored"
                    : `Sponsored - ${Math.ceil(daysLeft)} day${
                        Math.ceil(daysLeft) > 1 ? "s" : ""
                      } left`}
                </span>
              ) : null
            ) : null}
            <span
              className={`${tagClasses} self-end mb-xs mr-xs transition-all lg:group-hover:bg-opacity-0`}
              data-testid="video-card-duration"
            >
              {secondsToTime(duration)}
            </span>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="lg:hidden" data-testid="video-card-description-mobile">
          {description ? (
            <p
              className={`gn-text-xs mt-sm ${
                isDark ? "text-white" : "text-900"
              }`}
            >
              {description}
            </p>
          ) : null}
        </div>

        <span className="sr-only">View details for {title}</span>
      </div>
    );
  },
);

export default VideoCard;

VideoCard.propTypes = {
  mode: PropTypes.oneOf(["dark", "light"]),
  title: PropTypes.string,
  slug: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  images: PropTypes.shape({ tile: ImageSourcesType }).isRequired,
  sponsorExpDate: PropTypes.string,
  duration: PropTypes.number.isRequired,
  handleClick: PropTypes.func,
};

VideoCard.defaultProps = {
  mode: "dark",
  title: undefined,
  subtitle: undefined,
  description: undefined,
  sponsorExpDate: undefined,
  handleClick: () => {},
};
